const DEFAULT_INDEX_NAME = 'marketplaceListings';
const DEFAULT_RECOMMEND_MODEL = 'looking-similar';
const DEFAULT_FACETS = [
  'designer',
  'department',
  'category',
  'subCategory',
  'color',
  'size',
  'condition',
  'material',
];
const DEFAULT_QUERY_SUGGESTIONS_INDEX_SUFFIX = '_query_suggestions';
const FACET_QUERY_PARAM_BY_ATTRIBUTE = {
  department: 'pub_department',
  category: 'pub_category',
  subCategory: 'pub_subCategory',
  designer: 'pub_designer',
  color: 'pub_colour',
  size: 'pub_size',
  condition: 'pub_condition',
  material: 'pub_material',
};
const SELLER_FILTER_KEY = 'seller';
const SELLER_POP_NOUVELLE = 'popNouvelle';
const SELLER_PRIVATE_SELLERS = 'privateSellers';

const getAlgoliaConfig = () => ({
  appId: process.env.REACT_APP_ALGOLIA_APP_ID,
  searchKey: process.env.REACT_APP_ALGOLIA_SEARCH_KEY,
  indexName: process.env.REACT_APP_ALGOLIA_INDEX_NAME || DEFAULT_INDEX_NAME,
  recommendModel: process.env.REACT_APP_ALGOLIA_RECOMMEND_MODEL || DEFAULT_RECOMMEND_MODEL,
});

export const isClientSideAlgoliaConfigured = () => {
  const { appId, searchKey } = getAlgoliaConfig();
  return Boolean(appId && searchKey);
};

export const isAlgoliaFacetsEnabled = () => {
  const value = process.env.REACT_APP_ALGOLIA_ENABLE_FACETS;
  return value !== 'false' && value !== '0';
};

const buildFacetFilters = (filters, omittedFacetKey = null) => {
  const defaultFilters = ['inStock:true'];
  const mappings = [
    ['pub_department', 'department'],
    ['pub_category', 'category'],
    ['pub_subCategory', 'subCategory'],
    ['pub_designer', 'designer'],
    ['pub_colour', 'color'],
    ['pub_color', 'color'],
    ['pub_size', 'size'],
    ['pub_condition', 'condition'],
    ['pub_material', 'material'],
    ['saleCampaignKey', 'saleCampaignKeys'],
  ];

  return mappings.reduce((acc, [queryKey, facetKey]) => {
    if (facetKey === omittedFacetKey) {
      return acc;
    }

    const rawValue = filters?.[queryKey];
    if (!rawValue) {
      return acc;
    }

    const values = `${rawValue}`
      .replace(/^has_all:/, '')
      .replace(/^has_any:/, '')
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);

    if (!values.length) {
      return acc;
    }

    return [...acc, values.map(value => `${facetKey}:${value}`)];
  }, defaultFilters);
};

const normalizeObjectIDFilters = objectIDs => {
  if (!objectIDs) {
    return [];
  }

  const values = Array.isArray(objectIDs) ? objectIDs : `${objectIDs}`.split(',');
  return values.map(value => `${value}`.trim()).filter(Boolean);
};

const escapeAlgoliaFilterValue = value => `${value}`.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const getPopNouvelleUserId = () => process.env.REACT_APP_POP_NOUVELLE_USER;

const buildSellerFilter = filters => {
  const seller = filters?.[SELLER_FILTER_KEY];
  const popNouvelleUserId = getPopNouvelleUserId();

  if (!seller || !popNouvelleUserId) {
    return null;
  }

  const authorFilter = `authorId:"${escapeAlgoliaFilterValue(popNouvelleUserId)}"`;
  return seller === SELLER_POP_NOUVELLE
    ? authorFilter
    : seller === SELLER_PRIVATE_SELLERS
    ? `NOT ${authorFilter}`
    : null;
};

const buildFilters = (filters, { omitSeller = false } = {}) => {
  const objectIDs = normalizeObjectIDFilters(filters?.objectIDs);
  const objectIDFilter = objectIDs.length
    ? `(${objectIDs.map(id => `objectID:"${escapeAlgoliaFilterValue(id)}"`).join(' OR ')})`
    : null;
  const sellerFilter = omitSeller ? null : buildSellerFilter(filters);
  const filterParts = [objectIDFilter, sellerFilter].filter(Boolean);

  if (!filterParts.length) {
    return undefined;
  }

  return filterParts.join(' AND ');
};

const selectedFacetKeys = (filters, facets) => {
  const mappings = [
    ['pub_department', 'department'],
    ['pub_category', 'category'],
    ['pub_subCategory', 'subCategory'],
    ['pub_designer', 'designer'],
    ['pub_colour', 'color'],
    ['pub_color', 'color'],
    ['pub_size', 'size'],
    ['pub_condition', 'condition'],
    ['pub_material', 'material'],
    ['saleCampaignKey', 'saleCampaignKeys'],
  ];

  return mappings.reduce((acc, [queryKey, facetKey]) => {
    return filters?.[queryKey] && facets.includes(facetKey) && !acc.includes(facetKey)
      ? [...acc, facetKey]
      : acc;
  }, []);
};

const facetCountsFromResponse = (facetsData = {}, facets) =>
  facets.reduce((acc, facet) => {
    const queryParamName = FACET_QUERY_PARAM_BY_ATTRIBUTE[facet] || facet;
    return { ...acc, [queryParamName]: facetsData?.[facet] || {} };
  }, {});

const buildNumericFilters = filters => {
  const numericFilters = [];
  const price = filters?.price;
  const priceMinSubunits = filters?.priceMinSubunits;
  const priceMaxSubunits = filters?.priceMaxSubunits;

  if (priceMinSubunits || priceMaxSubunits) {
    if (priceMinSubunits) {
      numericFilters.push(`effectivePriceAmount>=${Number(priceMinSubunits)}`);
    }
    if (priceMaxSubunits) {
      numericFilters.push(`effectivePriceAmount<=${Number(priceMaxSubunits)}`);
    }
  } else if (price) {
    const [minPrice, maxPrice] = `${price}`.split(',');
    if (minPrice) {
      numericFilters.push(`effectivePriceAmount>=${Number(minPrice)}`);
    }
    if (maxPrice) {
      numericFilters.push(`effectivePriceAmount<=${Number(maxPrice)}`);
    }
  }

  return numericFilters;
};

const searchIndexNameForSort = sort => {
  const { indexName } = getAlgoliaConfig();

  if (!sort || sort === 'createdAt') {
    return `${indexName}_createdAt_desc`;
  }

  if (sort === '-price') {
    return `${indexName}_price_asc`;
  }

  if (sort === 'price') {
    return `${indexName}_price_desc`;
  }

  if (sort === 'saleDiscount') {
    return `${indexName}_saleDiscount_desc`;
  }

  return indexName;
};

const isPriceSort = sort => sort === '-price' || sort === 'price';
const isSaleDiscountSort = sort => sort === 'saleDiscount';
const shouldFallbackToPrimaryIndex = sort =>
  !sort || sort === 'createdAt' || isPriceSort(sort) || isSaleDiscountSort(sort);

const sortFallbackHits = (data, sort) => {
  const hits = [...(data?.hits || [])];

  if (sort === 'createdAt' || !sort) {
    return {
      ...data,
      hits: hits.sort((a, b) => {
        const aCreatedAt = Number(a?.createdAtTimestamp) || 0;
        const bCreatedAt = Number(b?.createdAtTimestamp) || 0;
        return bCreatedAt - aCreatedAt;
      }),
    };
  }

  if (isSaleDiscountSort(sort)) {
    return {
      ...data,
      hits: hits.sort((a, b) => {
        const aDiscount = Number.isFinite(Number(a?.saleDiscountPercentage))
          ? Number(a.saleDiscountPercentage)
          : null;
        const bDiscount = Number.isFinite(Number(b?.saleDiscountPercentage))
          ? Number(b.saleDiscountPercentage)
          : null;

        if (aDiscount == null && bDiscount == null) {
          return 0;
        }
        if (aDiscount == null) {
          return 1;
        }
        if (bDiscount == null) {
          return -1;
        }

        return bDiscount - aDiscount;
      }),
    };
  }

  if (!isPriceSort(sort)) {
    return data;
  }

  const direction = sort === '-price' ? 1 : -1;
  return {
    ...data,
    hits: hits.sort((a, b) => {
      const aPrice = Number.isFinite(Number(a?.effectivePriceAmount))
        ? Number(a.effectivePriceAmount)
        : null;
      const bPrice = Number.isFinite(Number(b?.effectivePriceAmount))
        ? Number(b.effectivePriceAmount)
        : null;

      if (aPrice == null && bPrice == null) {
        return 0;
      }
      if (aPrice == null) {
        return 1;
      }
      if (bPrice == null) {
        return -1;
      }

      return (aPrice - bPrice) * direction;
    }),
  };
};

const searchAlgoliaIndexClient = async ({ indexName, payload, signal }) => {
  const { appId, searchKey } = getAlgoliaConfig();

  if (!appId || !searchKey) {
    throw new Error('Client-side Algolia configuration is missing');
  }

  const response = await fetch(`https://${appId}-dsn.algolia.net/1/indexes/${indexName}/query`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Algolia-Application-Id': appId,
      'X-Algolia-API-Key': searchKey,
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const error = new Error(`Client-side Algolia search failed with status ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return response.json();
};

const encodeAlgoliaQueryParams = payload => {
  const params = new URLSearchParams();

  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    params.set(
      key,
      Array.isArray(value) || typeof value === 'object' ? JSON.stringify(value) : value
    );
  });

  return params.toString();
};

const searchAlgoliaMultipleQueriesClient = async ({ requests, signal }) => {
  const { appId, searchKey } = getAlgoliaConfig();

  if (!appId || !searchKey) {
    throw new Error('Client-side Algolia configuration is missing');
  }

  const response = await fetch(`https://${appId}-dsn.algolia.net/1/indexes/*/queries`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Algolia-Application-Id': appId,
      'X-Algolia-API-Key': searchKey,
    },
    body: JSON.stringify({
      requests: requests.map(request => ({
        indexName: request.indexName,
        params: encodeAlgoliaQueryParams(request.payload),
      })),
    }),
    signal,
  });

  if (!response.ok) {
    const error = new Error(
      `Client-side Algolia multi-search failed with status ${response.status}`
    );
    error.status = response.status;
    throw error;
  }

  return response.json();
};

const buildSellerCountRequests = ({ indexName, basePayload, filters }) => {
  if (!getPopNouvelleUserId()) {
    return [];
  }

  return [SELLER_POP_NOUVELLE, SELLER_PRIVATE_SELLERS].map(seller => ({
    seller,
    indexName,
    payload: {
      ...basePayload,
      page: 0,
      hitsPerPage: 0,
      facets: [],
      filters: buildFilters({ ...filters, [SELLER_FILTER_KEY]: seller }),
    },
  }));
};

const fetchFacetCounts = async ({
  indexName,
  basePayload,
  filters,
  facets,
  baseFacets,
  signal,
}) => {
  const baseFacetCounts = facetCountsFromResponse(baseFacets, facets);
  const selectedFacets = selectedFacetKeys(filters, facets);

  const sellerCountRequests = buildSellerCountRequests({ indexName, basePayload, filters });

  if (!selectedFacets.length && !sellerCountRequests.length) {
    return baseFacetCounts;
  }

  const facetRequests = selectedFacets.map(facet => ({
    indexName,
    payload: {
      ...basePayload,
      page: 0,
      hitsPerPage: 0,
      facets: [facet],
      facetFilters: buildFacetFilters(filters, facet),
    },
  }));

  let facetEntries;
  let sellerCounts = {};
  try {
    const data = await searchAlgoliaMultipleQueriesClient({
      requests: [...facetRequests, ...sellerCountRequests],
      signal,
    });
    facetEntries = selectedFacets.map((facet, index) => {
      const facetData = data?.results?.[index];
      return [FACET_QUERY_PARAM_BY_ATTRIBUTE[facet] || facet, facetData?.facets?.[facet] || {}];
    });
    sellerCounts = sellerCountRequests.reduce((acc, request, index) => {
      const sellerData = data?.results?.[selectedFacets.length + index];
      return { ...acc, [request.seller]: sellerData?.nbHits || 0 };
    }, {});
  } catch (e) {
    facetEntries = selectedFacets.map(facet => [
      FACET_QUERY_PARAM_BY_ATTRIBUTE[facet] || facet,
      {},
    ]);
    sellerCounts = {};
  }

  return facetEntries.reduce(
    (acc, [queryParamName, counts]) => {
      return { ...acc, [queryParamName]: counts };
    },
    { ...baseFacetCounts, seller: sellerCounts }
  );
};

export const searchAlgoliaListingsClient = async (params = {}) => {
  const {
    query = '',
    page = 0,
    hitsPerPage = 5,
    facets = DEFAULT_FACETS,
    includeFacetCounts = false,
    sort,
    signal,
    ...filters
  } = params;

  const shouldIncludeFacetCounts =
    includeFacetCounts === true || includeFacetCounts === 'true' || includeFacetCounts === '1';
  const requestedFacets = shouldIncludeFacetCounts ? facets : [];
  const filterParams = filters;
  const payload = {
    query: `${query}`.trim(),
    page: Number(page) || 0,
    hitsPerPage: Number(hitsPerPage) || 5,
    facetFilters: buildFacetFilters(filterParams),
    numericFilters: buildNumericFilters(filterParams),
    filters: buildFilters(filterParams),
    facets: requestedFacets,
  };

  const { indexName: primaryIndexName } = getAlgoliaConfig();
  const indexName = searchIndexNameForSort(sort);
  let searchIndexName = indexName;
  let data;

  try {
    data = await searchAlgoliaIndexClient({ indexName, payload, signal });
  } catch (e) {
    const canRetryPrimaryIndex = e?.status === 403 || e?.status === 404;
    if (
      !shouldFallbackToPrimaryIndex(sort) ||
      indexName === primaryIndexName ||
      !canRetryPrimaryIndex
    ) {
      throw e;
    }

    searchIndexName = primaryIndexName;
    data = sortFallbackHits(
      await searchAlgoliaIndexClient({ indexName: searchIndexName, payload, signal }),
      sort
    );
  }

  if (!shouldIncludeFacetCounts) {
    return data;
  }

  const facetCounts = await fetchFacetCounts({
    indexName: searchIndexName,
    basePayload: payload,
    filters: filterParams,
    facets: requestedFacets,
    baseFacets: data?.facets,
    signal,
  });

  return {
    ...data,
    facetCounts,
  };
};

export const searchAlgoliaQuerySuggestionsClient = async (params = {}) => {
  const { indexName } = getAlgoliaConfig();
  const {
    query = '',
    hitsPerPage = 4,
    signal,
    indexName: querySuggestionsIndexName = process.env.REACT_APP_ALGOLIA_QUERY_SUGGESTIONS_INDEX ||
      `${indexName}${DEFAULT_QUERY_SUGGESTIONS_INDEX_SUFFIX}`,
  } = params;

  const payload = {
    query: `${query}`.trim(),
    hitsPerPage: Number(hitsPerPage) || 4,
  };

  return searchAlgoliaIndexClient({
    indexName: querySuggestionsIndexName,
    payload,
    signal,
  });
};

export const searchAlgoliaAutocompleteClient = async (params = {}) => {
  const { indexName } = getAlgoliaConfig();
  const {
    query = '',
    hitsPerPage = 6,
    querySuggestionsHitsPerPage = 4,
    signal,
    indexName: querySuggestionsIndexName = process.env.REACT_APP_ALGOLIA_QUERY_SUGGESTIONS_INDEX ||
      `${indexName}${DEFAULT_QUERY_SUGGESTIONS_INDEX_SUFFIX}`,
    ...filters
  } = params;

  const normalizedQuery = `${query}`.trim();
  const listingPayload = {
    query: normalizedQuery,
    page: 0,
    hitsPerPage: Number(hitsPerPage) || 6,
    facetFilters: buildFacetFilters(filters),
    numericFilters: buildNumericFilters(filters),
    filters: buildFilters(filters),
    facets: [],
  };
  const querySuggestionsPayload = {
    query: normalizedQuery,
    hitsPerPage: Number(querySuggestionsHitsPerPage) || 4,
  };

  let querySuggestions;
  let listings;

  try {
    const data = await searchAlgoliaMultipleQueriesClient({
      signal,
      requests: [
        {
          indexName: querySuggestionsIndexName,
          payload: querySuggestionsPayload,
        },
        {
          indexName,
          payload: listingPayload,
        },
      ],
    });
    [querySuggestions, listings] = data?.results || [];
  } catch (e) {
    if (e?.status !== 404) {
      throw e;
    }

    listings = await searchAlgoliaIndexClient({
      indexName,
      payload: listingPayload,
      signal,
    });
    querySuggestions = { hits: [] };
  }

  return {
    querySuggestions: querySuggestions || { hits: [] },
    listings: listings || { hits: [] },
  };
};

export const getAlgoliaLookingSimilarClient = async (params = {}) => {
  const { appId, searchKey, indexName, recommendModel } = getAlgoliaConfig();
  const {
    objectID,
    maxRecommendations = 8,
    threshold = 0,
    signal,
    queryParameters,
    fallbackParameters,
  } = params;

  if (!appId || !searchKey) {
    throw new Error('Client-side Algolia configuration is missing');
  }

  if (!objectID) {
    return [];
  }

  const request = {
    indexName,
    model: recommendModel,
    objectID,
    maxRecommendations,
    threshold,
  };

  if (queryParameters) {
    request.queryParameters = queryParameters;
  }

  if (fallbackParameters) {
    request.fallbackParameters = fallbackParameters;
  }

  const response = await fetch(`https://${appId}-dsn.algolia.net/1/indexes/*/recommendations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Algolia-Application-Id': appId,
      'X-Algolia-API-Key': searchKey,
    },
    body: JSON.stringify({ requests: [request] }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Client-side Algolia recommendations failed with status ${response.status}`);
  }

  const data = await response.json();
  return data?.results?.[0]?.hits || [];
};
